import React, { useCallback, useEffect, useRef, useState } from 'react'
import useCurrentUser from '@/hooks/useCurrentUser'
import { motion } from 'framer-motion'
import {
  CheckIcon,
  CopyIcon,
  ExclamationTriangleIcon,
  ReloadIcon,
  Share1Icon,
} from '@radix-ui/react-icons'
import { Button } from '../../ui/button'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../ui/accordion'
import { ANIMATION_DELAYS, fadeUp, withDelay } from '../../../lib/animations'
import { cn } from '../../../lib/utils'

const DEFAULT_MESSAGE =
  'Something went wrong. Please contact the ResiDesk Team.'

const MAX_EXTERNAL_STACK_LINES = 10

function formatMetadataValue(value) {
  return value?.trim?.() || 'Unavailable'
}

function truncateStack(stack, maxLines) {
  if (!stack) return null
  const lines = stack.split('\n')
  if (lines.length <= maxLines) return stack
  return lines.slice(0, maxLines).join('\n') + `\n… ${lines.length - maxLines} more lines`
}

function buildErrorReport({
  errorMessage,
  url,
  timestamp,
  userAgent,
  stack,
  componentStack,
}) {
  return [
    'ResiDesk Error Report',
    `Message: ${errorMessage}`,
    `URL: ${url}`,
    `Timestamp: ${timestamp}`,
    `User Agent: ${userAgent}`,
    stack ? `Stack Trace:\n${stack}` : null,
    componentStack ? `Component Stack:\n${componentStack}` : null,
  ]
    .filter(Boolean)
    .join('\n\n')
}

function DetailRow({ label, value, className }) {
  return (
    <div
      className={cn(
        'grid gap-1.5 border-b border-border/50 py-3 last:border-b-0 sm:grid-cols-[120px_minmax(0,1fr)] sm:items-baseline',
        className
      )}
    >
      <span className="text-xs font-medium uppercase tracking-widest text-muted-foreground">
        {label}
      </span>
      <div className="min-w-0 text-sm leading-6 text-foreground break-words">
        {value}
      </div>
    </div>
  )
}

function useCopyAction() {
  const [state, setState] = useState('idle')
  const timerRef = useRef(null)

  useEffect(() => () => clearTimeout(timerRef.current), [])

  const copy = useCallback(async (text) => {
    try {
      await navigator.clipboard.writeText(text)
      setState('copied')
    } catch {
      setState('failed')
    }
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => setState('idle'), 2000)
  }, [])

  return { state, copy }
}

function ErrorMessageComponent({ error, info, clearError, message }) {
  const isInternal = useCurrentUser((user) => user?.isInternal) ?? false

  const errorMessage = error?.message || message || DEFAULT_MESSAGE
  const url = formatMetadataValue(window.location?.href)
  const [timestamp] = useState(() => new Date().toLocaleString())
  const userAgent = formatMetadataValue(window.navigator?.userAgent)
  const stack = error?.stack?.trim?.()
  const componentStack = info?.componentStack?.trim?.()
  const hasDebugDetails = Boolean(stack || componentStack)

  const fullReport = buildErrorReport({
    errorMessage,
    url,
    timestamp,
    userAgent,
    stack,
    componentStack,
  })

  const copyAction = useCopyAction()
  const slackAction = useCopyAction()

  const handleSlackShare = useCallback(() => {
    const slackReport = [
      ':rotating_light: *App Crash Report*',
      `> ${errorMessage}`,
      '',
      `*URL:* ${url}`,
      `*When:* ${timestamp}`,
      stack ? `\`\`\`\n${stack}\n\`\`\`` : null,
      componentStack ? `*Component stack:*\n\`\`\`\n${componentStack}\n\`\`\`` : null,
    ]
      .filter(Boolean)
      .join('\n')
    slackAction.copy(slackReport)
  }, [errorMessage, url, timestamp, stack, componentStack, slackAction])

  const briefStack = truncateStack(stack, MAX_EXTERNAL_STACK_LINES)

  return (
    <div className="min-h-screen bg-background px-4 py-12 text-foreground sm:px-6 lg:px-8">
      <div className="mx-auto flex min-h-[calc(100vh-6rem)] w-full max-w-2xl flex-col justify-center">
        <motion.div
          className="space-y-2"
          initial={fadeUp.initial}
          animate={fadeUp.animate}
          transition={fadeUp.transition}
        >
          <div className="mb-1 flex items-center gap-2.5 text-destructive">
            <ExclamationTriangleIcon className="h-4 w-4" />
            <span className="text-xs font-semibold uppercase tracking-widest">
              Application error
            </span>
          </div>
          <h1 className="text-3xl tracking-tight sm:text-4xl">
            Something went wrong
          </h1>
          <p className="max-w-lg text-sm leading-relaxed text-muted-foreground sm:text-base">
            {isInternal
              ? 'Copy the report below or share it directly on Slack for triage.'
              : 'We hit an unexpected error. You can retry or reload the page.'}
          </p>
        </motion.div>

        <motion.div
          className="mt-8 flex gap-3"
          initial={fadeUp.initial}
          animate={fadeUp.animate}
          transition={withDelay(fadeUp.transition, ANIMATION_DELAYS.short)}
        >
          {typeof clearError === 'function' ? (
            <Button onClick={clearError}>Try again</Button>
          ) : null}
          <Button
            variant="outline"
            onClick={() => window.location.reload()}
            className="gap-2"
          >
            <ReloadIcon className="h-3.5 w-3.5" />
            Reload page
          </Button>
        </motion.div>

        <motion.div
          className="mt-10"
          initial={fadeUp.initial}
          animate={fadeUp.animate}
          transition={withDelay(fadeUp.transition, ANIMATION_DELAYS.medium)}
        >
          {isInternal ? (
            <InternalErrorDetails
              errorMessage={errorMessage}
              url={url}
              timestamp={timestamp}
              userAgent={userAgent}
              stack={stack}
              componentStack={componentStack}
              hasDebugDetails={hasDebugDetails}
              fullReport={fullReport}
              copyAction={copyAction}
              slackAction={slackAction}
              onSlackShare={handleSlackShare}
            />
          ) : (
            <ExternalErrorDetails
              errorMessage={errorMessage}
              briefStack={briefStack}
              copyAction={copyAction}
              fullReport={fullReport}
            />
          )}
        </motion.div>
      </div>
    </div>
  )
}

function InternalErrorDetails({
  errorMessage,
  url,
  timestamp,
  userAgent,
  stack,
  componentStack,
  hasDebugDetails,
  fullReport,
  copyAction,
  slackAction,
  onSlackShare,
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-5 sm:p-6">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-sm">Error details</h2>
        <div className="flex items-center gap-2">
          <span className="sr-only" aria-live="polite">
            {copyAction.state === 'copied'
              ? 'Error report copied to clipboard'
              : copyAction.state === 'failed'
                ? 'Failed to copy error report'
                : ''}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 gap-1.5 text-xs text-muted-foreground"
            onClick={() => copyAction.copy(fullReport)}
          >
            {copyAction.state === 'copied' ? (
              <>
                <CheckIcon className="h-3.5 w-3.5" />
                Copied
              </>
            ) : (
              <>
                <CopyIcon className="h-3.5 w-3.5" />
                {copyAction.state === 'failed' ? 'Copy failed' : 'Copy all'}
              </>
            )}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 gap-1.5 text-xs text-muted-foreground"
            onClick={onSlackShare}
          >
            {slackAction.state === 'copied' ? (
              <>
                <CheckIcon className="h-3.5 w-3.5" />
                Copied for Slack
              </>
            ) : (
              <>
                <Share1Icon className="h-3.5 w-3.5" />
                Share on Slack
              </>
            )}
          </Button>
        </div>
      </div>

      <div>
        <DetailRow label="Message" value={errorMessage} />
        <DetailRow label="URL" value={url} />
        <DetailRow label="Timestamp" value={timestamp} />
        <DetailRow
          label="User agent"
          value={<span className="text-xs leading-6">{userAgent}</span>}
        />
      </div>

      {hasDebugDetails ? (
        <Accordion type="multiple" className="mt-4">
          {stack ? (
            <AccordionItem value="stack" className="border-border/60">
              <AccordionTrigger className="py-3 text-xs font-medium uppercase tracking-widest text-muted-foreground hover:no-underline">
                Stack trace
              </AccordionTrigger>
              <AccordionContent>
                <pre className="overflow-x-auto rounded-md bg-muted/50 p-3 text-xs leading-relaxed text-muted-foreground">
                  {stack}
                </pre>
              </AccordionContent>
            </AccordionItem>
          ) : null}

          {componentStack ? (
            <AccordionItem
              value="component-stack"
              className="border-border/60 last:border-b-0"
            >
              <AccordionTrigger className="py-3 text-xs font-medium uppercase tracking-widest text-muted-foreground hover:no-underline">
                Component stack
              </AccordionTrigger>
              <AccordionContent>
                <pre className="overflow-x-auto rounded-md bg-muted/50 p-3 text-xs leading-relaxed text-muted-foreground">
                  {componentStack}
                </pre>
              </AccordionContent>
            </AccordionItem>
          ) : null}
        </Accordion>
      ) : null}
    </div>
  )
}

function ExternalErrorDetails({
  errorMessage,
  briefStack,
  copyAction,
  fullReport,
}) {
  return (
    <Accordion type="single" collapsible className="rounded-lg border border-border bg-card">
      <AccordionItem value="details" className="border-none px-5 sm:px-6">
        <AccordionTrigger className="py-4 text-sm font-semibold hover:no-underline">
          Error details
        </AccordionTrigger>
        <AccordionContent>
          <div className="space-y-4 pb-2">
            <div className="rounded-md bg-muted/50 p-3">
              <p className="text-sm font-medium text-foreground">{errorMessage}</p>
              {briefStack ? (
                <pre className="mt-3 border-t border-border/50 pt-3 text-xs leading-relaxed text-muted-foreground">
                  {briefStack}
                </pre>
              ) : null}
            </div>

            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                className="h-8 gap-1.5 text-xs"
                onClick={() => copyAction.copy(fullReport)}
              >
                {copyAction.state === 'copied' ? (
                  <>
                    <CheckIcon className="h-3.5 w-3.5" />
                    Copied
                  </>
                ) : (
                  <>
                    <CopyIcon className="h-3.5 w-3.5" />
                    Copy error info
                  </>
                )}
              </Button>
              <span className="text-xs text-muted-foreground">
                Screenshot this or copy and send to your property team
              </span>
            </div>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  )
}

export default ErrorMessageComponent
